import { Device, DeviceManager } from '@4players/odin';
import { effect, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { DeviceSelection, MediaConfigStore } from '../../state/media-config.repository';
import { SelectOption } from '../../shared/components/form-fields/select-field/select-field';
import { MediaPermissionsService } from './media-permissions.service';
import { isMobile, isTauri } from '../../shared/helpers/platform';

/**
 * Device enumeration, selection and resolution for audio in/out and video.
 *
 * Selections are persisted as {id, name} pairs (see DeviceSelection) and
 * resolved against the live device list before every use: by id first, by
 * name second (browsers rotate deviceIds), falling back to the default
 * device. Capture pipelines live in LocalStreamService; permission state in
 * MediaPermissionsService.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  mapDeviceOptions = (devices: Device[]) => {
    const filtered = devices
      .filter((device) => {
        return !!device.name;
      })
      .map((device) => {
        return {
          name: device.name,
          id: device.id,
          isDefault: device.isDefault,
        };
      });
    if (filtered.length === 0) {
      return [{ name: 'Default', id: 'default', isDefault: true }];
    } else {
      return filtered;
    }
  };

  readonly audioInputDevices$ = new BehaviorSubject<Device[]>([]);
  readonly audioInputDeviceOptions$: Observable<SelectOption[]> = this.audioInputDevices$.pipe(
    map(this.mapDeviceOptions)
  );

  private readonly _activeAudioInputDevice = new BehaviorSubject<Device | undefined>(undefined);
  // The name is part of the comparison because the default entry keeps the
  // constant id 'default' while its name tracks the OS default device — a
  // label-only change must still reach the UI.
  readonly activeAudioInputDevice$ = this._activeAudioInputDevice.pipe(
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id && prev?.name === curr?.name)
  );

  get activeAudioInputDevice() {
    return this._activeAudioInputDevice.value;
  }

  activeAudioInputDeviceOption$: Observable<SelectOption> = this.activeAudioInputDevice$.pipe(
    map((device) => {
      if (device && device.name.length > 0) {
        return {
          name: device.name,
          id: device.id,
          isDefault: device.isDefault,
        };
      }
      return { name: 'Default', id: 'default', isDefault: true };
    })
  );

  readonly audioOutputDevices$ = new BehaviorSubject<Device[]>([]);
  readonly audioOutputDeviceOptions$: Observable<SelectOption[]> = this.audioOutputDevices$.pipe(
    map(this.mapDeviceOptions)
  );

  /**
   * Whether the browser lets us route playback to a chosen output device.
   * The odin web plugin switches the sink via `AudioContext.setSinkId`, which
   * only Chromium ships — Firefox and Safari lack it, and the HTMLAudioElement
   * fallback is left disabled, so picking an output device there is a no-op.
   * When false, the UI shows a hint to change the device in the OS/browser
   * instead of an (ineffective) device picker. The native app plays through
   * the plugin, which always routes to the chosen device (on phones the
   * speaker/earpiece entries switch the audio route).
   */
  readonly audioOutputSelectionSupported =
    isTauri || (typeof AudioContext !== 'undefined' && 'setSinkId' in AudioContext.prototype);

  private readonly _activeAudioOutputDevice = new BehaviorSubject<Device | undefined>(undefined);
  readonly activeAudioOutputDevice$ = this._activeAudioOutputDevice.pipe(
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id && prev?.name === curr?.name)
  );

  get activeAudioOutputDevice(): Device | undefined {
    return this._activeAudioOutputDevice.value;
  }

  readonly activeAudioOutputDeviceOption$: Observable<SelectOption> =
    this.activeAudioOutputDevice$.pipe(
      map((device) => {
        if (device && device.name.length > 0) {
          return {
            name: device.name,
            id: device.id,
            isDefault: device.isDefault,
          };
        }
        return { name: 'Default', id: 'default', isDefault: true };
      })
    );
  private _audioDeviceError = new BehaviorSubject<string | null>(null);
  readonly audioDeviceError$ = this._audioDeviceError.asObservable();

  readonly videoDevices$: BehaviorSubject<MediaDeviceInfo[]> = new BehaviorSubject<
    MediaDeviceInfo[]
  >([]);
  readonly videoDeviceOptions$: Observable<SelectOption[]> = this.videoDevices$.pipe(
    map((devices) => {
      if (devices.length === 0) {
        return [{ name: 'Default', id: 'default', isDefault: true }];
      }
      return devices
        .filter((device) => {
          return !!device.label;
        })
        .map((device) => {
          return {
            name: device.label,
            id: device.deviceId,
            isDefault: false,
          };
        });
    })
  );
  private readonly _selectedVideoDevice = new BehaviorSubject<SelectOption>({
    name: 'Default',
    id: 'default',
  });
  readonly selectedVideoDevice$ = this._selectedVideoDevice.asObservable();

  private _videoDeviceError = new BehaviorSubject<string | null>(null);
  readonly videoDeviceError$ = this._videoDeviceError.asObservable();

  private _deviceChangeTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private _mediaConfigStore: MediaConfigStore,
    private _permissions: MediaPermissionsService
  ) {
    // Seed the active audio input/output devices as soon as mic permission is
    // granted, independent of which component is mounted. The prep-area and
    // settings-audio components have their own refresh effects, but neither
    // mounts on the room-login or persistAppState reload paths — without this,
    // _activeAudioInputDevice would stay `undefined`, leaving device popovers
    // empty and forcing the SDK to fall back to whatever the browser picks.
    effect(() => {
      if (this._permissions.microphoneAccess() === 'granted') {
        this.refreshAudioInputDevices().catch(console.error);
        this.refreshAudioOutputDevices().catch(console.error);
      }
    });

    // Keep the audio device lists live while we may enumerate: a device
    // plugged in mid-call appears in the pickers, and a re-plugged stored
    // device is re-selected by the resolve→heal path in the refreshes.
    // Bursts (one event per affected endpoint) are debounced, mirroring the
    // plugin's own 250 ms coalescing. Gated on granted permission so the
    // refresh never triggers the Firefox label probe from a cold state.
    // Root-provided service — lives for the app, no teardown needed.
    if (isMobile) void this.watchNativeAudioRoute();
    navigator.mediaDevices?.addEventListener('devicechange', () => {
      clearTimeout(this._deviceChangeTimer);
      this._deviceChangeTimer = setTimeout(() => {
        if (this._permissions.microphoneAccess() !== 'granted') return;
        this.refreshAudioInputDevices().catch(console.error);
        this.refreshAudioOutputDevices().catch(console.error);
      }, 250);
    });
  }

  /**
   * Phones list their outputs as audio routes (speaker, earpiece and
   * whatever headset is connected) picked like any other output device. The
   * plugin reports route changes — a headset plugged in or removed — so the
   * output list follows them.
   */
  private async watchNativeAudioRoute() {
    const { listen } = await import('@tauri-apps/api/event');
    await listen<string>('odin://audio-route-changed', (event) => {
      console.info('audio route changed', event.payload);
      this.refreshAudioOutputDevices().catch(console.error);
    });
  }

  /**
   * Resolves a persisted device selection against the currently available
   * devices: by id first, by name second (browsers rotate deviceIds — see
   * DeviceSelection), falling back to the default device.
   *
   * Same matching semantics as `DeviceManager.resolveDevice` (@4players/odin
   * 1.10.0), kept local on purpose: the refreshes must resolve against the
   * exact device-list snapshot they publish, while the SDK helper
   * re-enumerates internally (an extra roundtrip per refresh).
   */
  private resolveAudioDevice(
    selection: DeviceSelection | null,
    devices: Device[]
  ): Device | undefined {
    return (
      devices.find((device) => !!selection?.id && device.id === selection.id) ??
      devices.find((device) => !!selection?.name && device.name === selection.name) ??
      devices.find((device) => device.isDefault)
    );
  }

  /**
   * Resolves the persisted camera selection against the given device list:
   * by id first, by name second. There is no browser-level "default camera"
   * concept, so an unresolved selection yields undefined — callers fall
   * back to an unconstrained getUserMedia (browser picks).
   */
  private resolveVideoDevice(
    selection: DeviceSelection | null,
    devices: MediaDeviceInfo[]
  ): MediaDeviceInfo | undefined {
    return (
      devices.find((device) => !!selection?.id && device.deviceId === selection.id) ??
      devices.find((device) => !!selection?.name && device.label === selection.name)
    );
  }

  /**
   * Backfills the persisted selection after a successful resolution: a
   * name-match rewrites a rotated id, an id-match backfills a missing name.
   * A default-fallback (selected device currently absent) does NOT touch
   * the store, so a temporarily unplugged device is re-selected when it
   * returns.
   */
  private healSelection(
    key: 'audioInputDevice' | 'audioOutputDevice' | 'videoDevice',
    selection: DeviceSelection | null,
    resolved: DeviceSelection
  ) {
    if (!selection) return;
    if (selection.id === resolved.id && selection.name === resolved.name) return;
    const matchedById = resolved.id === selection.id;
    const matchedByName = !!selection.name && resolved.name === selection.name;
    if (!matchedById && !matchedByName) return;
    this._mediaConfigStore.update({ [key]: { ...resolved } });
  }

  /**
   * Resolves the persisted camera selection to a currently-valid deviceId
   * for a getUserMedia constraint (healing the stored selection on the
   * way), or undefined when there is no selection or it cannot be resolved
   * — callers must then open unconstrained (browser default camera). A
   * stored id must never reach a deviceId constraint unverified: rotated or
   * stale ids reject with OverconstrainedError and no camera opens at all.
   */
  async resolveVideoDeviceIdForCapture(): Promise<string | undefined> {
    const selection = this._mediaConfigStore.value.videoDevice;
    if (!selection) return undefined;
    const devices = await this.getVideoDevices().catch(() => [] as MediaDeviceInfo[]);
    const resolved = this.resolveVideoDevice(selection, devices);
    if (!resolved) return undefined;
    this.healSelection('videoDevice', selection, {
      id: resolved.deviceId,
      name: resolved.label,
    });
    return resolved.deviceId || undefined;
  }

  async refreshAudioInputDevices() {
    const errorText = 'Can not get the audio device info. Please check permissions.';
    try {
      const audioDevices = await DeviceManager.listAudioInputs();
      const selection = this._mediaConfigStore.value.audioInputDevice;
      const activeDevice = this.resolveAudioDevice(selection, audioDevices);
      if (activeDevice) {
        this._activeAudioInputDevice.next(activeDevice);
        this.healSelection('audioInputDevice', selection, {
          id: activeDevice.id,
          name: activeDevice.name,
        });
      }
      this.audioInputDevices$.next(audioDevices);
    } catch (e) {
      console.error(e);
      this._audioDeviceError.next(errorText);
    }
  }

  /**
   * Refreshes the deviceList and resolves the persisted selection against
   * it (id → name → default device).
   */
  async refreshAudioOutputDevices(): Promise<Device | undefined> {
    try {
      const audioDevices = await DeviceManager.listAudioOutputs();
      const selection = this._mediaConfigStore.value.audioOutputDevice;
      const activeDevice = this.resolveAudioDevice(selection, audioDevices);
      if (activeDevice) {
        this._activeAudioOutputDevice.next(activeDevice);
        this.healSelection('audioOutputDevice', selection, {
          id: activeDevice.id,
          name: activeDevice.name,
        });
      }
      this.audioOutputDevices$.next(audioDevices);
    } catch (e) {
      console.error(e);
      this._activeAudioOutputDevice.next(undefined);
    }
    return this.activeAudioOutputDevice;
  }

  /**
   * Load all audio and video input devices.
   */
  async getVideoDevices(): Promise<MediaDeviceInfo[]> {
    if (isTauri) return this.getNativeVideoDevices();
    const videoDevices: MediaDeviceInfo[] = [];
    try {
      const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();

      devices.forEach((device) => {
        if (device.kind === 'videoinput') videoDevices.push(device);
      });

      return videoDevices;
    } catch (err) {
      throw new Error(`Could not fetch the userMedia\n${err}\n`);
    }
  }

  /**
   * Cameras of the native capture pipeline, shaped like the browser's
   * MediaDeviceInfo so selection and persistence stay shared. Their ids are
   * what `openCamera` expects; the WebView's own enumeration would not match.
   */
  private async getNativeVideoDevices(): Promise<MediaDeviceInfo[]> {
    const { listVideoDevices } = await import('@4players/odin-plugin-tauri/capture');
    const devices = await listVideoDevices();
    return devices.map(
      ({ deviceId, label }) =>
        ({
          deviceId,
          label,
          groupId: '',
          kind: 'videoinput',
          toJSON: () => ({ deviceId, label, groupId: '', kind: 'videoinput' }),
        }) satisfies MediaDeviceInfo
    );
  }

  get activeVideoDeviceId(): string {
    return this._selectedVideoDevice.value.id;
  }

  // On Firefox, there is still the issue, that the List is empty before a MediaStream was started.
  async refreshVideoDevices() {
    try {
      const devices = await this.getVideoDevices();
      this.videoDevices$.next(devices);
      const selection = this._mediaConfigStore.value.videoDevice;
      const device = this.resolveVideoDevice(selection, devices);
      if (device) {
        this._selectedVideoDevice.next({
          name: device.label.length ? device.label : 'Default',
          id: device.deviceId.length ? device.deviceId : 'default',
        });
        this.healSelection('videoDevice', selection, {
          id: device.deviceId,
          name: device.label,
        });
      } else {
        // Selection currently unresolvable (or none) — show Default so the
        // UI never claims a device that isn't in the options list. The
        // stored selection is kept; see healSelection.
        this._selectedVideoDevice.next({ name: 'Default', id: 'default' });
      }
      this._videoDeviceError.next(null);
    } catch (e) {
      console.warn(e);
      this._videoDeviceError.next('Can not get the video device info. Please check permissions.');
    }
  }

  selectAudioInputDeviceById(id: string) {
    const device = this.audioInputDevices$.value.find((device) => {
      return device.id === id;
    });
    if (device) {
      this._activeAudioInputDevice.next(device);
      this._mediaConfigStore.update({
        audioInputDevice: { id: device.id, name: device.name },
      });
    }
  }

  selectAudioOutputDeviceById(id: string) {
    const device = this.audioOutputDevices$.value.find((device) => {
      return device.id === id;
    });
    if (device) {
      this._activeAudioOutputDevice.next(device);
      this._mediaConfigStore.update({
        audioOutputDevice: { id: device.id, name: device.name },
      });
    }
  }

  selectVideoDevice(id?: string) {
    if (!id || id === 'default') {
      this._mediaConfigStore.update({ videoDevice: null });
    } else {
      const device = this.videoDevices$.value.find((device) => device.deviceId === id);
      this._mediaConfigStore.update({
        videoDevice: { id, name: device?.label ?? '' },
      });
    }
  }
}
